import { TransportResult } from './editContextTransport';
import { EditContextWarning, RowCounts } from './writeTypes';

/**
 * W6 — reconciliation (spec section 5.6).
 *
 * The rules, each one a way a write can look fine and not be:
 * 1. counts must equal `plan.expectedCounts` exactly. A shortfall is the documented TOCTOU race —
 *    a concurrent delete yields a SILENT 0-row update with no error (`edit_context.js:493-494`) —
 *    so a mismatch aborts the queue rather than shrugging (E11).
 * 2. a non-empty `warnings[]` ABORTS. The stock modal only reports warnings and stays open
 *    (`SelectiveEditModal.tsx:167-175`); Basil aborts and audits (E10).
 * 3. 422 aborts with `details[]` recorded verbatim and no retry (E6).
 * 4. a resolved 503, or any reply with a missing `success` or missing counts, is a FAILURE
 *    (`api.js:60-72`) — never a 0-row success (E19).
 * 5. a 401 is an abort with the reason `session-lost`.
 */

export type ReconcileFailure =
  | 'transport'
  | 'not-success'
  | 'missing-counts'
  | 'warnings'
  | 'count-mismatch';

export interface Reconciliation {
  ok: boolean;
  failure: ReconcileFailure | null;
  message: string | null;
  counts: RowCounts | null;
  expectedCounts: RowCounts | null;
  warnings: EditContextWarning[];
  details: Array<{ message: string }>;
  /** false when the request may have been applied but the outcome could not be observed. */
  outcomeKnown: boolean;
}

function countsOf(result: TransportResult): RowCounts | null {
  const { reply } = result;

  if (!reply
    || typeof reply.updatedNodes !== 'number'
    || typeof reply.updatedSteps !== 'number'
    || typeof reply.updatedAnswers !== 'number') {
    return null;
  }

  return {
    updatedNodes: reply.updatedNodes,
    updatedSteps: reply.updatedSteps,
    updatedAnswers: reply.updatedAnswers,
  };
}

export function countsEqual(left: RowCounts | null, right: RowCounts | null): boolean {
  if (!left || !right) {
    return false;
  }

  return left.updatedNodes === right.updatedNodes
    && left.updatedSteps === right.updatedSteps
    && left.updatedAnswers === right.updatedAnswers;
}

/**
 * Turn one transport result into a verdict on whether the chunk may be considered applied.
 *
 * @param result what the transport observed
 * @param expectedCounts the chunk's own `plan.expectedCounts`; `null` in whole-branch mode, where
 *  the engine reports no per-row counts at all and the structural delta is checked instead
 */
export function reconcileEditContextReply(
  result: TransportResult,
  expectedCounts: RowCounts | null,
): Reconciliation {
  const warnings = (result.reply && Array.isArray(result.reply.warnings)) ? result.reply.warnings : [];
  const base = {
    counts: countsOf(result),
    expectedCounts,
    warnings,
    details: result.details,
    outcomeKnown: result.outcomeKnown,
  };

  if (!result.ok || result.failure) {
    return {
      ...base,
      ok: false,
      failure: result.failure === 'missing-counts' ? 'missing-counts' : 'transport',
      message: result.message || "Basil couldn't tell whether this change went through.",
    };
  }

  if (!result.reply || result.reply.success !== true) {
    return {
      ...base, ok: false, failure: 'not-success', message: "Your change was sent, but Basil couldn't confirm it.",
    };
  }

  if (!base.counts) {
    return {
      ...base,
      ok: false,
      failure: 'missing-counts',
      message: "Your change was sent, but Basil couldn't confirm what it did.",
      outcomeKnown: false,
    };
  }

  if (warnings.length > 0) {
    return {
      ...base,
      ok: false,
      failure: 'warnings',
      message: `Your change was sent, but ${warnings.length} part${warnings.length === 1 ? '' : 's'} of it ${warnings.length === 1 ? "wasn't" : "weren't"} kept. Basil stopped here.`,
    };
  }

  if (expectedCounts && !countsEqual(base.counts, expectedCounts)) {
    return {
      ...base,
      ok: false,
      failure: 'count-mismatch',
      message: "Your change was sent, but the episode doesn't look the way Basil expected.",
    };
  }

  return {
    ...base, ok: true, failure: null, message: null,
  };
}
